"use client";

import {
  MdOutlineNavigateNext,
  MdNavigateNext,
  MdRocketLaunch,
  MdSchool,
  MdGroups,
  MdEmojiEmotions,
} from "react-icons/md";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { m, useInView } from "framer-motion";
import { getApiUrl } from "@/lib/apiUrl";
import { getImageUrl } from "@/lib/imageUrl";
import { stripDangerousChars } from "@/lib/validation";
import OtpChannelNotice from "@/components/OtpChannelNotice";
import CareerAppliedModal from "./CareerAppliedModal";
import { GLOBAL_PARTNERS } from "@/lib/homepageStats";

const GENDER_OPTIONS = ["Male", "Female"];

// Mirrors the server-side validation in CustomerVilvaBusiness's
// CareerApplicationController::store() exactly — client-side checks here are
// just faster feedback, the server is still the real enforcement.
const QUALIFICATION_REGEX = /^[\p{L}0-9\s,.\-()&]+$/u;
const ADDRESS_REGEX = /^[\p{L}0-9\s,.\-\/#]+$/u;
const SKILLS_REGEX = /^[\p{L}0-9\s,.\-+#\/]+$/u;
const RESUME_MAX_BYTES = 5 * 1024 * 1024;
const RESUME_ALLOWED_EXT = ["pdf", "doc", "docx"];

const fifteenYearsAgoISO = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 15);
  return d.toISOString().split("T")[0];
};

const CULTURE_PILLARS = [
  {
    icon: MdRocketLaunch,
    title: "Real Growth, Real Impact",
    desc: "Ship features used by real Indian businesses — not busywork stuck in a backlog.",
  },
  {
    icon: MdSchool,
    title: "Learn Fast, Grow Faster",
    desc: "Hands-on exposure across tech, marketing & ops, with people happy to mentor you.",
  },
  {
    icon: MdGroups,
    title: "Big Team, Big Ownership",
    desc: "No getting lost in a large org — your work is visible and it matters from day one.",
  },
  {
    icon: MdEmojiEmotions,
    title: "Genuinely Friendly Culture",
    desc: "A supportive, easygoing team that celebrates wins together, big or small.",
  },
];

// Founded date + Chennai office count are stable facts; partner count is the
// same live figure the homepage/about-us pages show (see GlobalPartnersCount.tsx)
// instead of a hardcoded "50+" that drifts from the real number.
const CULTURE_STATS = [
  { value: "2015", label: "Founded" },
  { value: `${GLOBAL_PARTNERS}+`, label: "Partners Trusted" },
  { value: "2", label: "Chennai Offices" },
];

interface Job {
  id: number;
  career_role_id: number;
  title: string;
  slug: string;
  location: string;
  employment_type: string;
  salary_range: string;
  job_description: string;
  requirements: string;
  posted_at: string;
  closing_date: string;
  created_at: string;
  updated_at: string;
  status: string;
}

interface Career {
  id: number;
  name: string;
  slug: string;
  description: string;
  status: string;
  jobs: Job[];
}

const CareerDetailsPageView = ({ initialJob = null }: { initialJob?: Job | null }) => {
  const params = useParams();
  const slug = params.slug as string;
  const router = useRouter();
  const [careers, setCareers] = useState<Career[]>([]);
  // Seed from the server-rendered job so the H1 + description are present in the initial HTML
  // (SEO) and known roles render instantly instead of flashing a spinner.
  const [selectedJob, setSelectedJob] = useState<Job | null>(initialJob);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputRefs: MutableRefObject<(HTMLInputElement | null)[]> = useRef([]);
  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", honeypot: "" });
  const [showOtpForm, setShowOtpForm] = useState(false);
  // Which channel the OTP actually went out on (WhatsApp -> SMS -> email),
  // and the masked destination — both decided by the backend at send time.
  const [otpChannel, setOtpChannel] = useState<string | null>(null);
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [loading, setLoading] = useState(!initialJob);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    mobile?: string;
    message?: string;
    captcha?: string;
    country?: string;
    honeypot?: string;
  }>({});
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const cultureRef = useRef<HTMLDivElement>(null);
  const cultureInView = useInView(cultureRef, { once: true, amount: 0.2 });

  // Step 2 — shown inline right after OTP verification, in place of the old
  // redirect to career.vilvabusiness.com/register (now retired).
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [detailsData, setDetailsData] = useState({
    dob: "",
    gender: "",
    qualification: "",
    experience: "",
    address: "",
    skills: "",
  });
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [detailsErrors, setDetailsErrors] = useState<Record<string, string>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Same static JSON snapshot pattern as blogs.json/pages.json — regenerated
  // by growsuite whenever a role or job posting changes
  // (CMSController::generateCareersJson()), instead of hitting the encrypted
  // /careers API on every page load.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(getImageUrl("/vilvabusiness/careers.json"), {
          next: { revalidate: 60 },
        });
        const parsedData = await response.json();
        setCareers(parsedData.careers ?? []);

        let foundJob: Job | null = null;
        for (const career of parsedData.careers ?? []) {
          // Match on slug alone — it is unique per posting. The old
          // `&& job.career_role_id === career.id` guard silently hid valid jobs whenever the
          // backend's career_role_id didn't match the parent career id.
          const job = career.jobs.find((job: Job) => job.slug === slug);
          if (job) {
            foundJob = job;
            break;
          }
        }

        // Fall back to the server-provided job so a known role never shows "unavailable".
        setSelectedJob(foundJob || initialJob || null);
      } catch (error) {
        console.error("Error fetching careers:", error);
        setSelectedJob((prev) => prev ?? initialJob ?? null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const recaptchaSiteKey = "6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-";

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setFormData((prev) => ({ ...prev, "g-recaptcha-response": token || "" }));
    setErrors((prev) => ({ ...prev, captcha: "" }));
  };

  const handleInput = (index: number, value: string) => {
    const newValue = value.replace(/[^0-9]/g, "").slice(0, 1);
    const newOtp = [...otp];
    newOtp[index] = newValue;
    setOtp(newOtp);

    if (newValue && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const clean = /email/i.test(name) ? value : stripDangerousChars(value);
    setFormData((prev) => ({
      ...prev,
      [name]: clean,
    }));
    setErrors((prev) => ({ ...prev, [name]: "", message: "" }));
  };

  /** Points a 409 "already applied" response at the actual field(s) it's about, instead of a generic message. */
  const applyConflictErrors = (error: any) => {
    const conflictFields: string[] = error?.response?.data?.conflict_fields || [];
    const emailConflict = conflictFields.includes("email");
    const mobileConflict = conflictFields.includes("mobile");

    if (emailConflict && !mobileConflict) {
      setErrors((prev) => ({ ...prev, email: "You've already applied for a job with this email." }));
    } else if (mobileConflict && !emailConflict) {
      setErrors((prev) => ({ ...prev, mobile: "You've already applied for a job with this mobile number." }));
    } else {
      setErrors((prev) => ({
        ...prev,
        message: error?.response?.data?.message || "You have already submitted an application with this email or mobile number.",
      }));
    }
  };

  const isFormComplete = () => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.mobile.trim() !== "" &&
      formData.honeypot.trim() === "" &&
      recaptchaToken !== null
    );
  };

  const handleGenerateOtp = async () => {
    if (!selectedJob) {
      console.error("No job selected");
      return;
    }
    setButtonLoading(true);
    try {
      const response = await axios.post(
        getApiUrl(`/career/request-otp`),
        {
          mobile: formData.mobile,
          email: formData.email,
          name: formData.name,
          title: selectedJob.title,
          honeypot: formData.honeypot,
        },
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.status === 200) {
        setShowOtpForm(true);
        setOtpGenerated(true);
        // The backend tries WhatsApp, then SMS, then the email typed on this
        // form, and reports which one actually took.
        setOtpChannel(response.data?.channel ?? null);
        setOtpSentTo(response.data?.sent_to ?? null);
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        applyConflictErrors(error);
      } else {
        console.error("Error generating OTP:", error);
        setErrors((prev) => ({
          ...prev,
          message: "Failed to generate OTP. Please try again.",
        }));
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp(["", "", "", ""]);
    setButtonLoading(true);
    try {
      if (!selectedJob) {
        console.error("No job selected");
        return;
      }
      const response = await axios.post(
        getApiUrl(`/career/request-otp`),
        {
          mobile: formData.mobile,
          email: formData.email,
          name: formData.name,
          title: selectedJob.title,
          honeypot: formData.honeypot,
        },
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.status === 200) {
        setOtpChannel(response.data?.channel ?? null);
        setOtpSentTo(response.data?.sent_to ?? null);
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        applyConflictErrors(error);
      } else {
        console.error("Error resending OTP:", error);
        setErrors((prev) => ({
          ...prev,
          message: "Failed to resend OTP. Please try again.",
        }));
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    if (!selectedJob) {
      console.error("No job selected");
      return;
    }
    setButtonLoading(true);
    try {
      const randpmValvalue = Math.random().toString(36).substring(2, 10);
      const response = await axios.post(
        getApiUrl(`/career/submit-otp`),
        {
          mobile: formData.mobile,
          email: formData.email,
          name: formData.name,
          title: selectedJob.title,
          otp: otp.join(""),
          honeypot: formData.honeypot,
        },
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.status === 200) {
        // OTP section closes, the full application form opens right here —
        // no more redirect to a separate domain.
        setShowOtpForm(false);
        setShowDetailsForm(true);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setErrors((prev) => ({
        ...prev,
        message: "Failed to verify OTP. Please try again.",
      }));
    } finally {
      setButtonLoading(false);
    }
  };

  const isOtpComplete = () => {
    return otp.every((digit) => digit !== "");
  };

  const handleDetailsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const clean = name === "experience" || name === "dob" || name === "gender" ? value : stripDangerousChars(value);
    setDetailsData((prev) => ({ ...prev, [name]: clean }));
    setDetailsErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResumeFile(e.target.files?.[0] ?? null);
    setDetailsErrors((prev) => ({ ...prev, resume: "" }));
  };

  const isDetailsComplete = () => {
    return (
      detailsData.dob.trim() !== "" &&
      detailsData.gender.trim() !== "" &&
      detailsData.qualification.trim() !== "" &&
      detailsData.experience.toString().trim() !== "" &&
      detailsData.address.trim() !== "" &&
      detailsData.skills.trim() !== "" &&
      resumeFile !== null
    );
  };

  const validateDetails = () => {
    const errs: Record<string, string> = {};

    if (!detailsData.dob || detailsData.dob > fifteenYearsAgoISO()) {
      errs.dob = "You must be at least 15 years old to apply.";
    }
    if (!QUALIFICATION_REGEX.test(detailsData.qualification)) {
      errs.qualification = "Qualification contains characters that are not allowed.";
    }
    if (Number.isNaN(Number(detailsData.experience)) || Number(detailsData.experience) < 0 || Number(detailsData.experience) > 60) {
      errs.experience = "Enter a valid number of years (0–60).";
    }
    if (!ADDRESS_REGEX.test(detailsData.address)) {
      errs.address = "Address contains characters that are not allowed.";
    }
    if (!SKILLS_REGEX.test(detailsData.skills)) {
      errs.skills = "Skills contain characters that are not allowed.";
    }
    if (!resumeFile) {
      errs.resume = "Please attach your resume.";
    } else {
      const ext = resumeFile.name.split(".").pop()?.toLowerCase();
      if (!ext || !RESUME_ALLOWED_EXT.includes(ext)) {
        errs.resume = "Resume must be a PDF, DOC, or DOCX file.";
      } else if (resumeFile.size > RESUME_MAX_BYTES) {
        errs.resume = "Resume must be under 5MB.";
      }
    }

    setDetailsErrors((prev) => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const handleDetailsSubmit = async () => {
    if (!selectedJob || !validateDetails()) return;

    setDetailsLoading(true);
    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("email", formData.email);
      form.append("phone", formData.mobile);
      form.append("role", selectedJob.title);
      form.append("dob", detailsData.dob);
      form.append("gender", detailsData.gender);
      form.append("qualification", detailsData.qualification);
      form.append("experience", detailsData.experience);
      form.append("address", detailsData.address);
      form.append("skills", detailsData.skills);
      if (resumeFile) form.append("resume", resumeFile);

      await axios.post(getApiUrl("/career/apply"), form, {
        headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
      });

      setShowDetailsForm(false);
      setApplicationSubmitted(true);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setDetailsErrors({ general: error.response.data?.message || "You have already submitted an application with this email or mobile number." });
      } else if (error?.response?.status === 422 && error.response.data?.errors) {
        const serverErrors: Record<string, string> = {};
        Object.entries(error.response.data.errors).forEach(([key, value]) => {
          serverErrors[key] = Array.isArray(value) ? String(value[0]) : String(value);
        });
        setDetailsErrors(serverErrors);
      } else {
        setDetailsErrors({ general: "Something went wrong. Please try again." });
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 md:mb-8 text-sm">
          <Link href="/" className="text-[#195AFF] font-LatinkaSemiBold hover:underline font-medium">
            Home
          </Link>
          <MdNavigateNext size={20} className="text-[#70708D]" />
          <Link href="/careers" className="text-[#195AFF] font-LatinkaSemiBold hover:underline font-medium">
            Career
          </Link>
          <MdNavigateNext size={20} className="text-[#70708D]" />
          <span className="text-[#70708D] font-LatinkaSemiBold font-medium line-clamp-1">
            {selectedJob ? selectedJob.title : "Job Details"}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#195AFF] border-t-transparent"></div>
              <span className="text-[#195AFF] font-semibold">Loading...</span>
            </div>
          </div>
        ) : !selectedJob ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
            <p className="font-NeueMontreal text-[#2C2845] font-bold text-xl mb-6">
              This position may be filled or unavailable.
              <br />
              Please return to the Careers page to view other openings.
            </p>
            <Link
              href="/careers"
              className="bg-[#195AFF] text-white px-8 py-3 rounded-[4px] font-semibold hover:bg-[#1449CC] transition-colors"
            >
              Back to Careers
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left Column - Job Details */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="bg-white rounded-xl border border-[#E4E9F0] p-6 md:p-8">
                {/* Job Header */}
                <div className="mb-6 pb-6 border-b border-[#E4E9F0]">
                  <h1 className="font-NeueMontreal text-2xl md:text-3xl font-bold text-[#000000] mb-3">
                    {selectedJob.title}
                  </h1>
                  <div className="flex flex-wrap  items-center gap-3 text-sm">
                    <span className="px-3 py-1 bg-[#E6EDFF] font-LatinkaSemiBold text-[#195AFF] rounded-full font-medium">
                      {selectedJob.employment_type}
                    </span>
                    <span className="flex items-center gap-1 font-LatinkaSemiBold text-[#70708D]">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      {selectedJob.location}
                    </span>
                  </div>
                </div>

                {/* Job Description */}
                <div className="mb-6">
                  <h2 className="font-NeueMontreal text-xl font-bold text-[#000000] mb-3">Description</h2>
                  <p className="font-Schibsted_Grotesk text-[#393333] leading-relaxed whitespace-pre-line">
                    {selectedJob.job_description}
                  </p>
                </div>

                {/* Requirements */}
                <div className="mb-6">
                  <h2 className="font-NeueMontreal text-xl font-bold text-[#000000]  mb-3">Requirements</h2>
                  <ul className="space-y-2  font-LatinkaSemiBold">
                    {selectedJob.requirements
                      .split(". ")
                      .filter((req) => req.trim())
                      .map((req, index) => (
                        <li key={`${index}-${req.trim()}`} className="flex  font-LatinkaSemiBold items-start gap-3 text-[#393333]">
                          <span className="text-[#195AFF] mt-1">✦</span>
                          <span className=" font-LatinkaSemiBold">{req.trim()}</span>
                        </li>
                      ))}
                  </ul>
                </div>

                {/* Salary Range */}
                <div className="pt-6 border-t border-[#E4E9F0]">
                  <h2 className="font-NeueMontreal text-xl font-bold text-[#000000] mb-2">Salary Range</h2>
                  <p className="text-[#195AFF] font-semibold text-lg">{selectedJob.salary_range}</p>
                </div>
              </div>
            </div>

            {/* Right Column - Application Form */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="bg-gradient-to-br from-[#195AFF] to-[#1449CC] rounded-xl p-6 md:p-8 sticky top-8">
                {applicationSubmitted ? (
                  <div className="text-center py-6">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/20 text-[28px]">
                      🎉
                    </div>
                    <h2 className="font-NeueMontreal text-2xl font-bold text-white mb-2">Application Submitted</h2>
                    <p className="text-white/80 text-sm">Check your email shortly for your task.</p>
                  </div>
                ) : showDetailsForm ? (
                  <>
                    <h2 className="font-NeueMontreal text-2xl font-bold text-white mb-2">Complete Your Application</h2>
                    <p className="text-white/80 mb-6 text-sm">Just a few more details and you&apos;re done</p>

                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                      {detailsErrors.general && (
                        <p className="text-red-300 text-xs text-center">{detailsErrors.general}</p>
                      )}

                      <div>
                        <label htmlFor="dob" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Date of Birth *
                        </label>
                        <input
                          type="date"
                          id="dob"
                          name="dob"
                          max={fifteenYearsAgoISO()}
                          className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                          value={detailsData.dob}
                          onChange={handleDetailsChange}
                        />
                        {detailsErrors.dob && <p className="text-red-300 text-xs mt-1">{detailsErrors.dob}</p>}
                      </div>

                      <div>
                        <label htmlFor="gender" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Gender *
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                          value={detailsData.gender}
                          onChange={handleDetailsChange}
                        >
                          <option value="">Select gender</option>
                          {GENDER_OPTIONS.map((g) => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                        {detailsErrors.gender && <p className="text-red-300 text-xs mt-1">{detailsErrors.gender}</p>}
                      </div>

                      <div>
                        <label htmlFor="qualification" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Qualification *
                        </label>
                        <input
                          type="text"
                          id="qualification"
                          name="qualification"
                          placeholder="e.g. B.E Computer Science"
                          className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                          value={detailsData.qualification}
                          onChange={handleDetailsChange}
                        />
                        {detailsErrors.qualification && <p className="text-red-300 text-xs mt-1">{detailsErrors.qualification}</p>}
                      </div>

                      <div>
                        <label htmlFor="experience" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Experience (years) *
                        </label>
                        <input
                          type="number"
                          id="experience"
                          name="experience"
                          min={0}
                          max={60}
                          step="0.5"
                          placeholder="e.g. 2"
                          className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                          value={detailsData.experience}
                          onChange={handleDetailsChange}
                        />
                        {detailsErrors.experience && <p className="text-red-300 text-xs mt-1">{detailsErrors.experience}</p>}
                      </div>

                      <div>
                        <label htmlFor="address" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Address *
                        </label>
                        <textarea
                          id="address"
                          name="address"
                          rows={3}
                          placeholder="Your current address"
                          className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors resize-none"
                          value={detailsData.address}
                          onChange={handleDetailsChange}
                        />
                        {detailsErrors.address && <p className="text-red-300 text-xs mt-1">{detailsErrors.address}</p>}
                      </div>

                      <div>
                        <label htmlFor="skills" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Skills *
                        </label>
                        <input
                          type="text"
                          id="skills"
                          name="skills"
                          placeholder="e.g. React, Node.js, Figma"
                          className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                          value={detailsData.skills}
                          onChange={handleDetailsChange}
                        />
                        {detailsErrors.skills && <p className="text-red-300 text-xs mt-1">{detailsErrors.skills}</p>}
                      </div>

                      <div>
                        <label htmlFor="resume" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Resume *
                        </label>
                        <input
                          type="file"
                          id="resume"
                          name="resume"
                          accept=".pdf,.doc,.docx"
                          className="w-full px-4 py-2.5 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] font-LatinkaSemiBold text-sm focus:outline-none focus:border-[#FFA500] transition-colors file:mr-3 file:py-1.5 file:px-3 file:rounded-[4px] file:border-0 file:bg-[#E6EDFF] file:text-[#195AFF] file:font-LatinkaSemiBold"
                          onChange={handleResumeChange}
                        />
                        <p className="text-white/70 text-xs mt-1">PDF, DOC, or DOCX — up to 5MB</p>
                        {detailsErrors.resume && <p className="text-red-300 text-xs mt-1">{detailsErrors.resume}</p>}
                      </div>

                      <button
                        type="button"
                        onClick={handleDetailsSubmit}
                        disabled={!isDetailsComplete() || detailsLoading}
                        className="w-full py-3 px-6 rounded-[4px] bg-white text-[#195AFF] font-LatinkaSemiBold text-lg hover:bg-[#F0F4FF] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                      >
                        {detailsLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-3 border-[#195AFF] border-t-transparent mr-2"></div>
                            Submitting...
                          </>
                        ) : (
                          "Submit Application"
                        )}
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                <h2 className="font-NeueMontreal text-2xl font-bold text-white mb-2">Apply Now</h2>
                <p className="text-white/80 mb-6 text-sm">Fill in your details to apply for this position</p>

                <form className="space-y-4">
                  {/* Name Input */}
                  <div>
                    <label htmlFor="name" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={otpGenerated || buttonLoading}
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={otpGenerated || buttonLoading}
                    />
                    {errors.email && <p className="text-red-300 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Mobile Input */}
                  <div>
                    <label htmlFor="mobile" className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      placeholder="Enter your mobile number"
                      className="w-full px-4 py-3 rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] placeholder:text-[#9CA3AF] font-LatinkaSemiBold focus:outline-none focus:border-[#FFA500] transition-colors"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      disabled={otpGenerated || buttonLoading}
                    />
                    {errors.mobile && <p className="text-red-300 text-xs mt-1">{errors.mobile}</p>}
                  </div>

                  {/* Honeypot */}
                  <div style={{ display: "none" }}>
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* reCAPTCHA */}
                  <div className="flex justify-center py-2">
                    <ReCAPTCHA
                      sitekey={recaptchaSiteKey}
                      onChange={handleRecaptchaChange}
                      theme="light"
                    />
                  </div>
                  {errors.captcha && (
                    <p className="text-red-300 text-xs text-center">{errors.captcha}</p>
                  )}

                  {errors.message && (
                    <p className="text-red-300 text-xs text-center">{errors.message}</p>
                  )}

                  {/* OTP Form */}
                  {showOtpForm && (
                    <div className="space-y-4">
                      <OtpChannelNotice channel={otpChannel} sentTo={otpSentTo} />
                      <div>
                        <label className="block font-LatinkaSemiBold text-white text-sm font-medium mb-2">
                          Enter OTP *
                        </label>
                        <div className="flex gap-2 justify-between">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              type="text"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleInput(index, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(index, e)}
                              ref={(el) => {
                                inputRefs.current[index] = el;
                              }}
                              className="w-full h-14 text-center text-xl font-bold rounded-[4px] border-2 border-transparent bg-white text-[#2C2845] focus:outline-none focus:border-[#FFA500] transition-colors"
                            />
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={buttonLoading}
                        className="text-white text-sm font-LatinkaSemiBold font-medium underline hover:no-underline disabled:opacity-50"
                      >
                        {buttonLoading ? "Resending..." : "Resend OTP"}
                      </button>
                    </div>
                  )}

                  {/* Submit Button */}
                  {!otpGenerated ? (
                    <button
                      type="button"
                      onClick={handleGenerateOtp}
                      disabled={!isFormComplete() || buttonLoading}
                      className="w-full py-3 px-6 rounded-[4px] bg-white text-[#195AFF] font-LatinkaSemiBold text-lg hover:bg-[#F0F4FF] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    >
                      {buttonLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-3 border-[#195AFF] border-t-transparent mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        "Generate OTP"
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!isOtpComplete() || buttonLoading}
                      className="w-full py-3 px-6 rounded-[4px] bg-white text-[#195AFF] font-LatinkaSemiBold text-lg hover:bg-[#F0F4FF] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    >
                      {buttonLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-3 border-[#195AFF] border-t-transparent mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        "Apply Now"
                      )}
                    </button>
                  )}
                </form>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Why Join Us — shown regardless of job-load state so it isn't gated on a slow/failed
            careers fetch */}
        <m.section
          ref={cultureRef}
          initial={{ opacity: 0, y: 40 }}
          animate={cultureInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative mt-16 md:mt-24 overflow-hidden rounded-2xl border border-[#E4E9F0] bg-gradient-to-br from-[#F5F8FF] to-[#EAF0FF] px-6 py-12 md:px-12 md:py-16"
        >
          <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#6DA4F7]/20 blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-[#195AFF]/10 blur-[100px]" />

          <div className="relative mx-auto mb-12 max-w-2xl text-center">
            <span className="mb-4 inline-block rounded-full bg-[#E6EDFF] px-4 py-1 font-LatinkaSemiBold text-sm text-[#195AFF]">
              Life at Vilva Business
            </span>
            <h2 className="font-NeueMontreal text-2xl md:text-3xl font-bold text-[#000000] mb-3">
              Join Our Happy, Fast-Growing Team
            </h2>
            <p className="font-Schibsted_Grotesk text-[#70708D] leading-relaxed">
              We&apos;re a close-knit Chennai team building products real businesses use every
              day. Come build something meaningful with people who&apos;ll actually have your
              back.
            </p>
          </div>

          <div className="relative mb-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CULTURE_PILLARS.map((pillar, index) => (
              <m.div
                key={pillar.title}
                initial={{ opacity: 0, y: 24 }}
                animate={cultureInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                transition={{ duration: 0.5, delay: 0.15 + index * 0.1, ease: "easeOut" }}
                whileHover={{ y: -6 }}
                className="rounded-xl border border-[#E4E9F0] bg-white p-6 text-center shadow-[0px_4px_16px_rgba(25,90,255,0.06)] transition-shadow hover:shadow-[0px_8px_24px_rgba(25,90,255,0.12)]"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#E6EDFF] text-[#195AFF]">
                  <pillar.icon size={24} />
                </div>
                <h3 className="font-NeueMontreal text-base font-bold text-[#000000] mb-2">
                  {pillar.title}
                </h3>
                <p className="font-Schibsted_Grotesk text-sm text-[#70708D] leading-relaxed">
                  {pillar.desc}
                </p>
              </m.div>
            ))}
          </div>

          <m.div
            initial={{ opacity: 0 }}
            animate={cultureInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="relative flex flex-wrap items-center justify-center gap-x-10 gap-y-4 border-t border-[#195AFF]/10 pt-8"
          >
            {CULTURE_STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-NeueMontreal text-xl md:text-2xl font-bold text-[#195AFF]">
                  {stat.value}
                </p>
                <p className="font-Schibsted_Grotesk text-xs text-[#70708D]">{stat.label}</p>
              </div>
            ))}
          </m.div>

          <div className="relative mt-10 text-center">
            <p className="font-Schibsted_Grotesk text-[#70708D] mb-4">
              Don&apos;t see the perfect fit yet? We&apos;re always happy to meet great people.
            </p>
            <Link
              href="/careers"
              className="inline-flex items-center gap-2 rounded-[4px] bg-[#195AFF] px-6 py-3 font-LatinkaSemiBold text-white transition-colors hover:bg-[#1449CC]"
            >
              Explore Other Openings
              <MdOutlineNavigateNext size={20} />
            </Link>
          </div>
        </m.section>
      </div>

      <CareerAppliedModal
        open={applicationSubmitted}
        onClose={() => setApplicationSubmitted(false)}
        onViewMoreJobs={() => router.push("/careers")}
      />
    </div>
  );
};

export default CareerDetailsPageView;
